import { AccessLevel } from "@/graphql/generated/types";

export interface ProjectForRights {
  company?: { id: string } | null;
  actions: { canUpdate: boolean; canManageAccess: boolean; canDelete: boolean };
  access?: ReadonlyArray<{ plan: AccessLevel; person: { id: string } }> | null;
}

/**
 * Why a project the viewer cannot write is read-only, where "you lack the right" would mislead:
 * `switchToCompany` — an admin of the owning company browsing as themselves;
 * `leaveCompany` — a personal project they could write, while they act as a company;
 * `companyInactive` / `companyInactiveReader` — a frozen company's project, for its admin / for an access row;
 * `askCompanyAdmin` — any other read-only company project, which has no owner to ask.
 */
export type ReadOnlyReason =
  | "switchToCompany"
  | "leaveCompany"
  | "companyInactive"
  | "companyInactiveReader"
  | "askCompanyAdmin"
  | null;

export interface ProjectRights {
  canUpdate: boolean;
  canManageAccess: boolean;
  canDelete: boolean;
  readOnlyReason: ReadOnlyReason;
}

const readOnly = (readOnlyReason: ReadOnlyReason): ProjectRights => ({
  canUpdate: false,
  canManageAccess: false,
  canDelete: false,
  readOnlyReason,
});

/**
 * What the viewer may do to a project in the context they act in (spark#1219). `Project.actions` names no
 * route — genesis grants a company's admin `canUpdate` although the write only passes with
 * `actingAsCompanyId` — so the acting context picks the route and `actions` decides within it, which keeps
 * an inactive company's freeze (genesis reports every action false then).
 */
export const projectRights = (
  project: ProjectForRights | null | undefined,
  viewerId: string | null,
  actingAsCompanyId: string | null,
): ProjectRights => {
  if (!project) return readOnly(null);
  const { actions, company } = project;
  const granted = { ...pick(actions), readOnlyReason: null };

  if (!company) {
    if (!actingAsCompanyId) return granted;
    return readOnly(actions.canUpdate ? "leaveCompany" : null);
  }
  // Only admins can act as a company, so here a missing canUpdate can only be the freeze.
  if (actingAsCompanyId === company.id) return actions.canUpdate ? granted : readOnly("companyInactive");
  if (actingAsCompanyId || viewerId === null) return readOnly("askCompanyAdmin");

  const ownWriteRow = (project.access ?? []).some(
    (entry) => entry.person.id === viewerId && entry.plan === AccessLevel.Write,
  );
  if (ownWriteRow) {
    return actions.canUpdate
      ? { canUpdate: true, canManageAccess: actions.canManageAccess, canDelete: false, readOnlyReason: null }
      : readOnly("companyInactiveReader");
  }
  // A company project has no creator route, so canUpdate without one's own row can only mean admin.
  return readOnly(actions.canUpdate ? "switchToCompany" : "askCompanyAdmin");
};

const pick = ({ canUpdate, canManageAccess, canDelete }: ProjectForRights["actions"]) => ({
  canUpdate,
  canManageAccess,
  canDelete,
});
